<template>
  <div style="display: flex">
    <div :id="legendId" class="legend"></div>
    <div :id="chartId" ref="chartReport" class="report-chart chartdiv float-left"></div>
  </div>
</template>

<script>
import { trainingItem } from '@/mixins/training-item.js'
import { measurementType, timePeriods, legendType, messageTypes } from '@/main.config'
import { formatDate } from '@/mixins/format-date.js'
import { trainingDate } from '@/mixins/training-date.js'
import i18n from '@/i18n.js'

import { legendAndChart } from '@/mixins/legend&chart'

import('@/styles/components/chart.less')

const legendUnit = {
  0: '%',
  1: ` ${i18n.t('unit.bpm')}`,
  2: ` ${i18n.t('unit.steps')}`,
  3: '%',
  4: '',
  5: ` ${i18n.t('unit.mmolL')}`
}

export default {
  name: 'CourseReportGraph',
  props: {
    trainings: {
      type: Array,
      required: true
    },
    trainings6mwt: {
      type: Array,
      required: true
    },
    allDoneTrainings: {
      type: Array,
      required: true
    },
    freeMeasurementsData: {
      type: Array,
      required: false
    },
    activeMeasurementType: {
      type: Number,
      required: true
    },
    activeTimeData: {
      type: Number,
      required: true
    },
    selectedDate: {
      type: Date,
      required: false
    }
  },
  mixins: [trainingItem, formatDate, trainingDate, legendAndChart],
  data: function() {
    return {
      chart: null,
      data: [],
      hrMaxData: [],
      hrAvrData: [],
      saturationData: [],
      listOfLegend: {
        0: this.$t('src/components/coursedatatable.8698'),
        1: this.$t('sessionsInfo.averageHeartRate'),
        2: this.$t('src/components/coursedatatable.3907'),
        3: this.$t('src/components/training/trainingresults6mwt.saturation'),
        4: this.$t('events.eventBP'),
        5: this.$t('src/components/chat.glucose')
      },
      selectedLegend: legendType.holdHR,
      selectedMeasurement: measurementType.standart,
      typeCreatedSeries: null,
      datePeriodStr: '',
      colorList: ['#E539AD', '#E57B39', '#3AE8BE', '#39ADE5', '#4C43E5', '#9AC724'],
      legendId: 'legend',
      chartId: 'chart',
      showDateInTooltip: true
    }
  },
  watch: {
    activeMeasurementType() {
      this.updateChartData()
    },
    activeTimeData() {
      this.updateChartData()
    },
    selectedDate() {
      this.updateChartData()
    }
  },
  methods: {
    handleLegendItemClick(index, legendItems) {
      this.selectedLegend = index
      this.chart.data = []
      this.updateChartData()

      if (index === legendType.pressure || this.typeCreatedSeries !== 'LineSeries') {
        this.handlePressureLegendClick(index)
      } else {
        this.series.stroke = this.chart.colors.getIndex(index).hex
        this.series.fill = this.chart.colors.getIndex(index).hex
      }

      this.$emit('selected:legend', index)

      legendItems.forEach((legendItem, i) => {
        legendItem.classList.remove('active-item')
        if (i === index) {
          legendItem.classList.add('active-item')
        }
      })
    },

    handlePressureLegendClick(index) {
      if (this.series) {
        this.series.dispose()
        while (this.chart.series.length > 0) {
          this.chart.series.removeIndex(0)
        }
      }

      if (index === legendType.pressure) {
        this.createColumnSeries()
        this.typeCreatedSeries = 'ColumnSeries'
      } else {
        this.createLineSeries()
        this.typeCreatedSeries = 'LineSeries'
      }

      this.series.stroke = this.chart.colors.getIndex(index).hex
      this.series.fill = this.chart.colors.getIndex(index).hex
    },
    getData() {
      const legendMap = {
        [legendType.holdHR]: 'rhytmogram_zone_percent',
        [legendType.averageHR]: 'rhytmogram_avr_hr',
        [legendType.distance]:
          this.activeMeasurementType === measurementType.standart ? 'step_count' : 'distance',
        [legendType.saturation]: 'saturation',
        [legendType.pressure]: 'pressure',
        [legendType.glucose]: 'glucose'
      }

      return this.getChartData(this.getOperatingData(), legendMap[this.selectedLegend])
    },
    getOperatingData() {
      let operatingData

      switch (this.activeMeasurementType) {
        case measurementType.standart:
          operatingData = {
            data: this.getDataWithTimeFilter(this.trainings),
            measurementType: measurementType.standart
          }
          break
        case measurementType.mwt6:
          operatingData = {
            data: this.getDataWithTimeFilter(this.trainings6mwt),
            measurementType: measurementType.mwt6
          }
          break
        case measurementType.free:
          operatingData = {
            data: this.getDataWithTimeFilter(this.freeMeasurementsData),
            measurementType: measurementType.free
          }
          break
        case measurementType.all:
          operatingData = {
            data: this.getAllMeasurementData(),
            measurementType: measurementType.all
          }
          break
        default:
          operatingData = {}
          break
      }

      return operatingData
    },
    getDataWithTimeFilter(array) {
      const currentDate = new Date()
      let timeDataStartDate
      let timeDataEndDate = currentDate
      switch (this.activeTimeData) {
        case timePeriods.allTime:
          this.label.text = ''
          return array
        case timePeriods.oneDay:
          timeDataStartDate = this.selectedDate
          timeDataEndDate = new Date(
            this.selectedDate.getFullYear(),
            this.selectedDate.getMonth(),
            this.selectedDate.getDate() + 1
          )
          break
        case timePeriods.oneWeek:
          timeDataStartDate = new Date(
            this.selectedDate.getFullYear(),
            this.selectedDate.getMonth(),
            this.selectedDate.getDate() - 6
          )
          timeDataEndDate = new Date(
            this.selectedDate.getFullYear(),
            this.selectedDate.getMonth(),
            this.selectedDate.getDate() + 1
          )
          break
        case timePeriods.oneMonth:
          timeDataStartDate = new Date(
            currentDate.getFullYear(),
            currentDate.getMonth(),
            currentDate.getDate() - 31
          )
          break
        case timePeriods.threeMonths:
          timeDataStartDate = new Date(
            currentDate.getFullYear(),
            currentDate.getMonth(),
            currentDate.getDate() - 92
          )
          break
      }
      const filteredArray = array.filter(item => {
        const itemDate = new Date(item.date_start)

        return itemDate >= timeDataStartDate && itemDate <= timeDataEndDate
      })
      this.datePeriodStr =
        this.activeTimeData === timePeriods.oneDay
          ? `${this.getFormattedDate(timeDataStartDate, true)}`
          : `${this.getFormattedDate(timeDataStartDate, true)} - ${this.getFormattedDate(
              timeDataEndDate,
              true
            )}`

      this.label.text = this.datePeriodStr
      return filteredArray
    },
    getChartData(operatingData, legend) {
      switch (operatingData.measurementType) {
        case measurementType.standart:
          return this.getChartDataFromTrainings(operatingData.data, legend)
        case measurementType.mwt6:
          return this.getChartDataFrom6MWT(operatingData.data, legend)
        case measurementType.free:
          return this.getChartDataFromMessages(operatingData.data, legend)
        case measurementType.all:
          return this.getChartDataFromAllData(operatingData.data, legend)
        default:
          return []
      }
    },
    trainingDate(training) {
      return `${this.getTrainingFormattedStartDate(training, true)} ${this.getTrainingStartTime(training)}`
    },
    trainingEndDate(training) {
      return `${this.getTrainingFormattedEndDate(training, true)} ${this.getTrainingEndTime(training)}`
    },
    getChartDataFromTrainings(operatingData, legend) {
      const chartDataFromTrainings = []
      const unit = legendUnit[this.selectedLegend]
      for (let i = 0; i < operatingData.length; i++) {
        let dataItem = {}
        let date = this.trainingDate(operatingData[i])
        let value, closeValue
        if (legend === 'saturation') {
          const parsedEvent = operatingData[i].events ? JSON.parse(operatingData[i].events) : []
          const saturation = parsedEvent.filter(item => item.episodeType === 'eventSpO2')
          if (saturation.length) {
            if (this.activeTimeData === timePeriods.oneDay) {
              for (let i = 0; i < saturation.length; i++) {
                const satItem = {}
                value = +saturation[i].value.replace(',', '.')
                const eventDate = this.addSecondsToDateString(date, saturation[i].beginTime)
                satItem.date = eventDate.slice(9)
                satItem.value = value
                satItem.closeValue = closeValue
                satItem.shaded = false
                satItem.shadedText = this.$t('entity.training')
                satItem.unit = unit

                chartDataFromTrainings.push(satItem)
              }
            } else {
              value = Math.min(...saturation.map(item => +item.value.replace(',', '.')))
              const eventDate = this.addSecondsToDateString(date, saturation[0].beginTime)
              dataItem.date = eventDate
              dataItem.value = value
              dataItem.closeValue = closeValue
              dataItem.shaded = false
              dataItem.shadedText = this.$t('entity.training')
              dataItem.unit = unit

              chartDataFromTrainings.push(dataItem)
            }
          }
        } else if (legend === 'pressure') {
          const parsedEvent = operatingData[i].events ? JSON.parse(operatingData[i].events) : []
          const pressure = parsedEvent.filter(item => item.episodeType === 'eventBP')
          if (pressure.length) {
            if (this.activeTimeData === timePeriods.oneDay) {
              for (let i = 0; i < pressure.length; i++) {
                const prsItem = {}
                const pressureArray = pressure[i].value.split('/')
                closeValue = +pressureArray[1]
                value = +pressureArray[0]
                const eventDate = this.addSecondsToDateString(date, pressure[i].beginTime)

                prsItem.date = eventDate.slice(9)
                prsItem.value = value
                prsItem.closeValue = closeValue
                prsItem.shaded = false
                prsItem.shadedText = this.$t('entity.training')
                prsItem.unit = unit

                chartDataFromTrainings.push(prsItem)
              }
            } else {
              let eventDate
              for (let i = 0; i < pressure.length; i++) {
                let pressureArray = pressure[i].value.split('/')
                if (!value || value < pressureArray[0]) {
                  closeValue = +pressureArray[1]
                  value = +pressureArray[0]
                  eventDate = this.addSecondsToDateString(date, pressure[i].beginTime)
                }
              }
              dataItem.date = eventDate
              dataItem.value = value
              dataItem.closeValue = closeValue
              dataItem.shaded = false
              dataItem.shadedText = this.$t('entity.training')
              dataItem.unit = unit

              chartDataFromTrainings.push(dataItem)
            }
          }
        } else {
          value = +operatingData[i][legend]
          dataItem.date = date
          dataItem.value = value
          dataItem.closeValue = closeValue
          dataItem.shaded = false
          dataItem.shadedText = this.$t('entity.training')
          dataItem.unit = unit

          chartDataFromTrainings.push(dataItem)
        }
      }
      return chartDataFromTrainings
    },
    getChartDataFrom6MWT(operatingData, legend) {
      const chartDataFromTrainings = []
      const unit = legend == 'distance' ? ` ${this.$t('unit.m')}` : legendUnit[this.selectedLegend]
      for (let i = 0; i < operatingData.length; i++) {
        let dataItem = {}
        let date =
          this.activeTimeData === timePeriods.oneDay
            ? this.trainingDate(operatingData[i]).slice(9)
            : this.trainingDate(operatingData[i])
        let value, closeValue
        if (legend === 'saturation') {
          let saturationBefore = operatingData[i].saturation_before
          if (saturationBefore) {
            let itemSaturationBefore = {
              date: date,
              value: +saturationBefore,
              shaded: false,
              shadedText: this.$t('entity.mwtStart'),
              unit: unit
            }
            chartDataFromTrainings.push(itemSaturationBefore)
          }
          let saturationAfter = operatingData[i].saturation_after
          if (saturationAfter) {
            let itemSaturationAfter = {
              date: this.trainingEndDate(operatingData[i]),
              value: +saturationAfter,
              shaded: false,
              shadedText: this.$t('entity.mwtFinal'),
              unit: unit
            }
            chartDataFromTrainings.push(itemSaturationAfter)
          }
        } else if (legend === 'pressure') {
          let pressureBeforeArray = operatingData[i].blood_pressure_before?.split('/')
          if (pressureBeforeArray && pressureBeforeArray.length) {
            let itemPressureBefore = {
              date: date,
              value: pressureBeforeArray[0],
              closeValue: pressureBeforeArray[1],
              shaded: false,
              shadedText: this.$t('entity.mwtStart'),
              unit: unit
            }
            chartDataFromTrainings.push(itemPressureBefore)
          }

          let pressureAfterArray = operatingData[i].blood_pressure_after?.split('/')
          if (pressureAfterArray && pressureAfterArray.length) {
            let itemPressureAfter = {
              date: this.trainingEndDate(operatingData[i]),
              value: pressureAfterArray[0],
              closeValue: pressureAfterArray[1],
              shaded: false,
              shadedText: this.$t('entity.mwtFinal'),
              unit: unit
            }
            chartDataFromTrainings.push(itemPressureAfter)
          }
        } else {
          value = +operatingData[i][legend]
          dataItem.date = date
          dataItem.value = value
          dataItem.closeValue = closeValue
          dataItem.shaded = false
          dataItem.shadedText = this.$t('src/pages/trainingview.wt6m')
          dataItem.unit = unit

          chartDataFromTrainings.push(dataItem)
        }
      }
      return chartDataFromTrainings
    },
    getChartDataFromMessages(operatingData, legend) {
      let dataFromMessages = []

      if (legend === 'saturation') {
        const saturationListData = operatingData.filter(item => item.type === messageTypes.saturation)
        for (let i = 0; i < saturationListData.length; i++) {
          let date = this.trainingDate(saturationListData[i])
          let idxMatchingDate = dataFromMessages.findIndex(item => item.date === date)
          if (idxMatchingDate >= 0) {
            dataFromMessages[idxMatchingDate].value = saturationListData[i].content
          } else {
            let dataItem = {
              date: this.activeTimeData === timePeriods.oneDay ? date.slice(9) : date,
              value: saturationListData[i].content,
              shaded: true,
              shadedText: this.$t('entity.freeMeasurement'),
              unit: legendUnit[this.selectedLegend]
            }
            dataFromMessages.push(dataItem)
          }
        }
      } else if (legend === 'pressure') {
        const pressureListData = operatingData.filter(item => item.type === messageTypes.pressure)
        for (let i = 0; i < pressureListData.length; i++) {
          let date = this.trainingDate(pressureListData[i])
          const pressureArrayFormat = pressureListData[i].content.split('/')
          let idxMatchingDate = dataFromMessages.findIndex(item => item.date === date)
          if (idxMatchingDate >= 0) {
            dataFromMessages[idxMatchingDate].value = +pressureArrayFormat[0]
            dataFromMessages[idxMatchingDate].closeValue = +pressureArrayFormat[1]
          } else {
            let dataItem = {
              date,
              closeValue: +pressureArrayFormat[1],
              value: +pressureArrayFormat[0],
              shaded: true,
              shadedText: this.$t('entity.freeMeasurement'),
              unit: legendUnit[this.selectedLegend]
            }
            dataFromMessages.push(dataItem)
          }
        }
      } else if (legend === 'glucose') {
        const glucoseListData = operatingData.filter(item => item.type === messageTypes.glucose)

        for (let i = 0; i < glucoseListData.length; i++) {
          let date = this.trainingDate(glucoseListData[i])
          let idxMatchingDate = dataFromMessages.findIndex(item => item.date === date)
          if (idxMatchingDate >= 0) {
            dataFromMessages[idxMatchingDate].value = glucoseListData[i].content
          } else {
            let dataItem = {
              date: this.activeTimeData === timePeriods.oneDay ? date.slice(9) : date,
              value: glucoseListData[i].content,
              shaded: true,
              shadedText: this.$t('entity.freeMeasurement'),
              unit: legendUnit[this.selectedLegend]
            }
            dataFromMessages.push(dataItem)
          }
        }
      }
      return dataFromMessages
    },
    getUpdatedData(selectedIndexLegend) {
      this.selectedLegend = selectedIndexLegend
      return this.getData()
    },
    getAllMeasurementData() {
      return {
        mwt: this.getDataWithTimeFilter(this.trainings6mwt),
        trainings: this.getDataWithTimeFilter(this.trainings),
        free: this.getDataWithTimeFilter(this.freeMeasurementsData)
      }
    },
    getChartDataFromAllData(operatingData, legend) {
      let chartDataMWT = this.getChartDataFrom6MWT(operatingData.mwt, legend)
      let chartDataFromTrainings = this.getChartDataFromTrainings(operatingData.trainings, legend)
      let chartDataFromMessages = this.getChartDataFromMessages(operatingData.free, legend)
      let allTrain = this.mergeArrays(chartDataFromTrainings, chartDataMWT)
      return this.mergeArrays(allTrain, chartDataFromMessages).filter(item => {
        return item.value
      })
    },
    mergeArrays(arrTrain, arrFreeMeas) {
      let mergedArray = arrTrain.concat(arrFreeMeas)
      mergedArray = mergedArray.sort((a, b) => {
        const dateA = new Date(a.date.replace(/(\d+).(\d+).(\d+)/, '$2 $1 $3'))
        const dateB = new Date(b.date.replace(/(\d+).(\d+).(\d+)/, '$2 $1 $3'))
        return dateA - dateB
      })

      return mergedArray
    },
    updateChartData() {
      this.chart.data = this.getData()
      this.chart.invalidateRawData()
    }
  }
}
</script>
